import Guid from "_shared/guids/guid";
import type { Resource } from "permissions/resources/permission-resource";
import AHDate from "_shared/dates/date";
import PermissionTemplateSchema, {
  PermissionTemplateJSON,
} from "permissions/templates/_schemas/permission-template-schema";
import { PermissionTemplateCtorParams } from "permissions/_views/types/permission-types";
import { resourceFromJSON } from "permissions/resources/utils";

export default class PermissionTemplate {
  public static fromJSON(templateJSON: PermissionTemplateJSON): PermissionTemplate {
    const parsed = PermissionTemplateSchema.deserialize(templateJSON);

    const resources: Resource[] = [];
    for (const resourceJSON of templateJSON.resources) {
      const resource = resourceFromJSON(resourceJSON);
      if (!resource) continue;
      resources.push(resource);
    }

    const ctorParams: PermissionTemplateCtorParams = {
      ...parsed,
      resources,
    };

    return new PermissionTemplate(ctorParams);
  }

  public static fromJSONArray(jsonArray: PermissionTemplateJSON[]): PermissionTemplate[] {
    return jsonArray.map((j) => PermissionTemplate.fromJSON(j));
  }

  public readonly id: Guid;
  public readonly userId: Guid;
  public readonly resources: Resource[];
  public readonly name: string;
  public readonly description?: string;
  public readonly createdDate?: AHDate;
  public readonly deletedDate?: AHDate;

  public constructor(params: PermissionTemplateCtorParams) {
    this.id = params.id;
    this.userId = params.userId;
    this.resources = params.resources;
    this.name = params.name;
    this.description = params.description;
    this.createdDate = params.createdDate;
    this.deletedDate = params.deletedDate;
  }

  public get isDeleted(): boolean {
    return !!this.deletedDate;
  }

  public toJSON(): PermissionTemplateJSON {
    const params = {
      id: this.id,
      userId: this.userId,
      resources: this.resources.map((res) => res.toJSON ? res.toJSON() : res),
      name: this.name,
      description: this.description,
      createdDate: this.createdDate,
      deletedDate: this.deletedDate,
    };
    return PermissionTemplateSchema.serialize(params);
  }

  public isEqualTo(other: PermissionTemplate): boolean {
    if (!other) return false;
    const idsEqual = Boolean((!this.id && !other.id) || (this.id && other.id && this.id.isEqualTo(other.id)));
    return (
      idsEqual &&
      this.userId.isEqualTo(other.userId) &&
      this.name === other.name &&
      this.description === other.description &&
      this.resources.length === other.resources.length &&
      this.resources.every((res) => other.resources.some((otherRes) => res.isEqualTo(otherRes)))
    );
  }

  public toString(): string {
    return `PermissionTemplate { 
      id: ${this.id.value}, 
      userId: ${this.userId.value}, 
      name: ${this.name}, 
      description: ${this.description ?? "undefined"} 
      createdDate: ${this.createdDate?.toString() ?? "undefined"},
      deletedDate: ${this.deletedDate?.toString() ?? "undefined"},
      resources: [${this.resources.map((res) => res.toString()).join(", ")}]
    }`;
  }
}
